/**
 * BulkImportModal — 3-screen XLSX import flow, in a modal.
 *
 * Reuses the existing bulk-upload step components verbatim:
 *   Screen 1 — `ActivityTypeStep` (What are you reporting?)
 *   Screen 2 — `BulkUploadStep` (download template + drop file)
 *   Screen 3 — `BulkImportPreview` (parsed rows with valid/error counts)
 *
 * Unlike the existing bulk-upload wizard step, this modal does NOT call
 * `createBulkImport` on accept — it returns the parsed rows + the source
 * `File` object to the parent, which appends them to its in-memory
 * staging state. Persistence happens once on the Entry-step Submit.
 */

import { useEffect, useMemo, useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { Button } from '@g17eco/ui-modern/components/ui/button';
import { downloadBlob } from '@utils/downloadBlob';
import { getErrorMessage } from '@utils/error';
import { useAppSelector } from '../../../../../reducers';
import { isStaff as isStaffSelector } from '../../../../../selectors/user';
import {
  useLazyGetActivityImportTemplateQuery,
  useLazyGetActivitySeedTemplateQuery,
} from '../../../api/emissions';
import { type SeedTemplateParams } from '../SeedTemplateModal';
import {
  EmissionScope,
  Scope1ActivityType,
  BULK_IMPORT_MAX_ROWS,
  type ActivityType,
  type CreateActivityRequest,
} from '../../../types/emissions';
import { parseImportFile, toActivityRequests, type ParseResult } from '../../../utils/bulkImportParser';
import { BulkUploadStep } from '../BulkUploadStep';
import { BulkImportPreview, ErrorSummary } from '../../BulkImportPreview';
import { OTHER_CATEGORIES, type SuggestedActivity } from '../AssetActivitiesStep';
import { ActivityCategoryPicker } from './ActivityCategoryPicker';

export interface BulkImportAccept {
  /** Activities ready to stage (validated rows from the parser). */
  activities: CreateActivityRequest[];
  /** The source XLSX/CSV file the user uploaded. Persisted on Submit so the BulkImport's `fileId` can point to it. */
  file: File;
  /** Scope chosen on screen 1 — needed by `POST /imports`. */
  scope: EmissionScope;
  /** Activity type (Scope 1 only) — passed through to `POST /imports`. */
  activityType?: ActivityType;
}

export interface BulkImportModalProps {
  isOpen: boolean;
  initiativeId: string;
  onAccept: (result: BulkImportAccept) => void;
  onClose: () => void;
}

type Screen = 'activity-type' | 'upload' | 'preview';

export const BulkImportModal = ({
  isOpen,
  initiativeId,
  onAccept,
  onClose,
}: BulkImportModalProps) => {
  const [screen, setScreen] = useState<Screen>('activity-type');
  const [scope, setScope] = useState<EmissionScope | ''>('');
  const [activityType, setActivityType] = useState<ActivityType | ''>('');
  const [importFile, setImportFile] = useState<File | undefined>();
  const [parseError, setParseError] = useState<string | null>(null);
  const [parseResult, setParseResult] = useState<ParseResult | null>(null);

  const [fetchTemplate, { isFetching: isDownloadingTemplate }] = useLazyGetActivityImportTemplateQuery();
  const [fetchSeedTemplate, { isFetching: isDownloadingSeedTemplate }] = useLazyGetActivitySeedTemplateQuery();
  const isStaff = useAppSelector(isStaffSelector);

  // Reset every time the modal closes — open + re-open should start fresh.
  useEffect(() => {
    if (!isOpen) {
      setScreen('activity-type');
      setScope('');
      setActivityType('');
      setImportFile(undefined);
      setParseError(null);
      setParseResult(null);
    }
  }, [isOpen]);

  const handleDownloadTemplate = async () => {
    if (!scope) {
      return;
    }
    const isScope1 = scope === EmissionScope.Scope1;
    const result = await fetchTemplate({
      initiativeId,
      scope,
      ...(isScope1 && activityType ? { activityType: activityType as Scope1ActivityType } : {}),
    });
    if (result.data) {
      const suffix = activityType ? `-${activityType}` : '';
      downloadBlob(result.data, `emissions-${scope}${suffix}-import-template.xlsx`);
      return;
    }
    const detail = result.error ? `: ${getErrorMessage(result.error)}` : '';
    setParseError(`Template download failed${detail}`);
  };

  // Staff-only seed template download — pre-fills the XLSX with realistic
  // demo rows. Same wiring as ActivityFormFlow's bulk-upload step.
  const handleDownloadSeedTemplate = async ({ rowCount, startYear, endYear }: SeedTemplateParams) => {
    if (!scope) {
      return;
    }
    const isScope1 = scope === EmissionScope.Scope1;
    const result = await fetchSeedTemplate({
      initiativeId,
      scope,
      ...(isScope1 && activityType ? { activityType: activityType as Scope1ActivityType } : {}),
      rowCount,
      startYear,
      endYear,
    });
    if (result.data) {
      const suffix = activityType ? `-${activityType}` : '';
      downloadBlob(result.data, `emissions-${scope}${suffix}-seed-template.xlsx`);
      return;
    }
    const detail = result.error ? `: ${getErrorMessage(result.error)}` : '';
    setParseError(`Seed template download failed${detail}`);
  };

  const handleFilesAdded = async (files: File[]) => {
    const file = files[0];
    if (!file || !scope) {
      return;
    }
    setImportFile(file);
    setParseError(null);
    try {
      const result = await parseImportFile(file, scope);
      setParseResult(result);
      setScreen('preview');
    } catch (error) {
      setParseError(error instanceof Error ? error.message : 'Failed to parse file');
      setParseResult(null);
    }
  };

  const handleFileRemove = () => {
    setImportFile(undefined);
    setParseResult(null);
    setParseError(null);
  };

  const handlePickSuggested = (suggested: SuggestedActivity) => {
    setScope(suggested.scope);
    // The XLSX template endpoint only accepts an activityType for Scope 1.
    // For Scope 2, the picker still narrows the user's reporting intent
    // (electricity vs. heat etc.) but we don't pass it to the template.
    setActivityType(
      suggested.scope === EmissionScope.Scope1
        ? (suggested.activityType as ActivityType)
        : '',
    );
    setScreen('upload');
  };

  const handlePreviewConfirm = () => {
    if (!parseResult || !importFile || !scope) {
      return;
    }
    const activities = toActivityRequests(parseResult);
    if (activities.length === 0 || activities.length > BULK_IMPORT_MAX_ROWS) {
      return;
    }
    onAccept({
      activities,
      file: importFile,
      scope,
      ...(activityType ? { activityType } : {}),
    });
  };

  const formDataForChildSteps = useMemo(
    () => ({
      scope: (scope || '') as EmissionScope | '',
      activityType: (activityType || '') as ActivityType | '',
    }),
    [scope, activityType],
  );

  const headerTitle = useMemo(() => {
    if (screen === 'activity-type') {
      return 'Bulk Import Activities';
    }
    if (screen === 'upload') {
      return 'Bulk import — upload file';
    }
    return 'Bulk import — preview';
  }, [screen]);

  const screenIndex: Record<Screen, number> = {
    'activity-type': 0,
    upload: 1,
    preview: 2,
  };

  const canConfirmPreview =
    parseResult !== null &&
    parseResult.validCount > 0 &&
    parseResult.validCount <= BULK_IMPORT_MAX_ROWS;

  return (
    <Modal isOpen={isOpen} toggle={onClose} centered size='xl' scrollable>
      <ModalHeader toggle={onClose}>{headerTitle}</ModalHeader>
      <ModalBody style={{ overflow: 'hidden', padding: 0 }}>
        <div
          style={{
            display: 'flex',
            transition: 'transform 250ms ease-in-out',
            transform: `translateX(-${screenIndex[screen] * (100 / 3)}%)`,
            width: '300%',
          }}
        >
          <div
            className='tw:p-4 tw:overflow-hidden'
            style={{ width: `${100 / 3}%`, flexShrink: 0, boxSizing: 'border-box' }}
          >
            <ActivityCategoryPicker
              categories={OTHER_CATEGORIES}
              actionLabel='Choose'
              onSelect={handlePickSuggested}
            />
          </div>
          <div
            className='tw:p-4 tw:overflow-hidden'
            style={{ width: `${100 / 3}%`, flexShrink: 0, boxSizing: 'border-box' }}
          >
            {scope && (
              <BulkUploadStep
                initiativeId={initiativeId}
                formData={formDataForChildSteps}
                importFile={importFile}
                parseError={parseError}
                isDownloadingTemplate={isDownloadingTemplate}
                onDownloadTemplate={handleDownloadTemplate}
                onFilesAdded={handleFilesAdded}
                onFileRemove={handleFileRemove}
                onBack={() => setScreen('activity-type')}
                isStaff={isStaff}
                onDownloadSeedTemplate={handleDownloadSeedTemplate}
                isDownloadingSeedTemplate={isDownloadingSeedTemplate}
              />
            )}
          </div>
          <div
            className='tw:p-4 tw:overflow-hidden'
            style={{ width: `${100 / 3}%`, flexShrink: 0, boxSizing: 'border-box' }}
          >
            {parseResult && (
              <>
                <BulkImportPreview parseResult={parseResult} />
                <ErrorSummary parseResult={parseResult} />
              </>
            )}
          </div>
        </div>
      </ModalBody>
      <ModalFooter style={{ minHeight: '4rem' }}>
        {screen === 'upload' && (
          <Button variant='secondary' onClick={() => setScreen('activity-type')}>
            Back
          </Button>
        )}
        {screen === 'preview' && (
          <>
            <Button
              variant='secondary'
              onClick={() => {
                setParseResult(null);
                setImportFile(undefined);
                setScreen('upload');
              }}
            >
              Back
            </Button>
            <Button
              className='tw:bg-emerald-700 tw:hover:bg-emerald-800 tw:text-white tw:border-0'
              onClick={handlePreviewConfirm}
              disabled={!canConfirmPreview}
            >
              Add {parseResult?.validCount ?? 0} {parseResult?.validCount === 1 ? 'activity' : 'activities'}
            </Button>
          </>
        )}
      </ModalFooter>
    </Modal>
  );
};
